import React, { useState } from 'react';

import { notification } from 'antd';
import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { get } from 'lodash';

import { getAccessToken } from '../auth/tokenProvider';

export type AxiosContextType = {
  axios?: AxiosInstance;
};
export const AxiosContext = React.createContext<AxiosContextType>({ axios });

/**
 * The token is looked up per request rather than captured once at provider
 * construction, which is what the pre-Cognito version did. A Cognito access
 * token is renewed in the background roughly every hour, so a token
 * snapshotted at mount goes stale while the tab is still open; resolving it
 * inside the interceptor means every request gets the current one and a
 * renewal is awaited if due.
 */
function configureAxiosInstance(axiosInstance: AxiosInstance) {
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      config = {
        baseURL: '/api',
        ...config,
      };

      const token = await getAccessToken();

      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error: Error) => Promise.reject(error),
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      if (error.request.responseURL.indexOf('locales') === -1) {
        // TODO handleError("generic", error)
      }

      if (get(error, 'response.status') === 401) {
        // No redirect and no credential teardown here. The access token lives
        // in the oidc-client-ts store, which the UserManager owns: a dead
        // session is removed by auth/tokenProvider.js when renewal fails, and
        // CognitoSessionGuard then starts sign-in. Clearing the impersonation
        // keys on a transient 401 would silently return the session to the
        // real admin while redux and the impersonation banner still claim
        // otherwise. The caller handles the rejection.
        return Promise.reject(error);
      }

      if (get(error, 'response.status') === 404) {
        // ignore 404
        return undefined;
      }

      notification.error({
        message: 'Oops!',
        description: 'An error occured!',
      });

      return Promise.reject(error);
    },
  );

  return axiosInstance;
}

export const AxiosContextProvider = (props: { children: any }) => {
  const [instance] = useState(() => configureAxiosInstance(axios.create()));

  return <AxiosContext.Provider value={{ axios: instance }}>{props?.children}</AxiosContext.Provider>;
};
